import LogisticTopMenu from '@components/logistic/LogisticTopMenu'
import React, { useState, useMemo, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import { FlexboxGrid, Panel, Table, Tag, IconButton, DatePicker, SelectPicker, Form, Button, Grid, Row, Col } from 'rsuite'
import ArrowDownLineIcon from '@rsuite/icons/ArrowDownLine'
import ArrowRightLineIcon from '@rsuite/icons/ArrowRightLine'
import SearchIcon from '@rsuite/icons/Search'

import {
  CartOutline,
  TimeOutline,
  CarOutline,
  CheckmarkCircleOutline
} from 'react-ionicons'

import { RSUITE_DATEPICKER_LOCALE } from '@@partner:config/rsuite'
import { useFetchAllCouriers } from '@@partner:graphql/query/employees/allCouriers.gql'
import { useFetchAllCourierWorkShifts } from '@@partner:graphql/query/courier/allCourierWorkShifts.gql'
import { GQL_ID } from '@@partner:types:graphql'
import { ORDER, WORKSHIFT } from '@hive/status'
import { formatOrderStatusLabel } from '@@partner:helpers/formatters/order'
import { formatWorkshiftStatusLabel } from '@@partner:helpers/formatters/workshift'
import { useFetchOrders } from '@@partner:graphql/query/order/allOrders.gql'
import { useFetchOrdersByStatus } from '@@partner:graphql/query/order/ordersByStatus.gql'
import StatCard from '@components/common/StatCard/StatCard'
import OrdersFilter from '@components/order/table/OrdersFilter'
import LogisticKanbanBoard from '@components/logistic/kanban/LogisticKanbanBoard'
import type { StatusGroup } from '@components/logistic/kanban/LogisticKanbanBoard'
import type { NestedFilter } from '@@partner:types:graphql'
import {
  COURIER_STATUSES,
  ORDER_STATUS_DELIVERED,
  ORDER_STATUS_PLACED,
  ORDER_STATUS_PAID,
  ORDER_STATUS_ASSEMBLY_IN_PROGRESS,
  ORDER_STATUS_ASSEMBLY_COMPLETE,
  ORDER_STATUS_WAITING_FOR_PAYMENT,
  ORDER_STATUS_WAITING_FOR_COURIER,
  ORDER_STATUS_ACCEPTED_BY_COURIER,
  ORDER_STATUS_DEVIVERY_IN_PROGRESS
} from '@@partner:config/order-status-codes'

const { Column, HeaderCell, Cell } = Table

// Интерфейсы для фильтров
interface CourierShiftFilters {
  date: Date | null;
  courierId: string | null;
}

type ShiftStatus =  typeof WORKSHIFT[keyof typeof WORKSHIFT]

interface IShift {
  id: GQL_ID;
  courier: string;
  shift: string;
  status: number;
  transport: {
    type:  'car' | 'bike' | 'foot' | 'scooter';
    name: string;
    licensePlate?: string;
  };
  zones: string[];
}

interface IOrder {
  id: string;
  address: string;
  status: number;
  courier: string;
}

const shiftStatusColors: Record<typeof WORKSHIFT[keyof typeof WORKSHIFT], any> = {
  [WORKSHIFT.STARTED]: 'green',
  [WORKSHIFT.FINISHED]: 'gray',
  [WORKSHIFT.INITIAL]: 'blue',
  [WORKSHIFT.WAITING_CONFIRMATION]: 'yellow',
  [WORKSHIFT.CANCELED]: 'gray'
}

const shiftStatusText: Record<ShiftStatus, string> = {
  [WORKSHIFT.INITIAL]: 'Запланирована',
  [WORKSHIFT.STARTED]: 'На смене',
  [WORKSHIFT.FINISHED]: 'Завершил',
  [WORKSHIFT.WAITING_CONFIRMATION]: 'Ожидает подтверждения',
  [WORKSHIFT.CANCELED]: 'Отменена'
}

// Группы статусов для Kanban логистики
const LOGISTIC_STATUS_GROUPS: StatusGroup[] = [
  {
    key: 'preparing',
    label: 'Готовятся',
    color: '#ff9800',
    statuses: [
      ORDER_STATUS_PLACED,               // 1
      ORDER_STATUS_PAID,                  // 2
      ORDER_STATUS_ASSEMBLY_IN_PROGRESS,  // 5
      ORDER_STATUS_ASSEMBLY_COMPLETE,     // 6
      ORDER_STATUS_WAITING_FOR_PAYMENT    // 10
    ]
  },
  {
    key: 'waiting_courier',
    label: 'Ожидают курьера',
    color: '#2196f3',
    statuses: [ ORDER_STATUS_WAITING_FOR_COURIER ] // 7
  },
  {
    key: 'courier_picked',
    label: 'Курьер забрал заказ',
    color: '#9c27b0',
    statuses: [ ORDER_STATUS_ACCEPTED_BY_COURIER ] // 4
  },
  {
    key: 'delivering',
    label: 'Доставляется',
    color: '#4caf50',
    statuses: [ ORDER_STATUS_DEVIVERY_IN_PROGRESS ] // 8
  }
]

// Все статусы логистики (объединение всех групп)
const ALL_LOGISTIC_STATUSES = LOGISTIC_STATUS_GROUPS.flatMap(g => g.statuses)

const transportIcon = {
  car: '🚗',
  scooter: '🛴',
  bike: '🚲',
  foot: '🚶'
}

const renderRowExpanded = (rowData?: IShift) => {
  if (!rowData) {
    return null
  }
  return (
    <div style={{ padding: '10px 20px' }}>
      <p><strong>Транспорт:</strong> {transportIcon[rowData.transport.type]} {rowData.transport.name} {rowData.transport.licensePlate && `(${rowData.transport.licensePlate})`}</p>
      <p><strong>Зоны доставки:</strong> {rowData.zones.join(', ')}</p>
    </div>
  )
}


export default function CouriersManagementPage () {
  const { brandSlug } = useParams<any>()
  const [ expandedRowKeys, setExpandedRowKeys ] = React.useState<(string | number)[]>([])

  // Пользовательский фильтр заказов (OrdersFilter)
  const [ userFilter, setUserFilter ] = useState<NestedFilter>({
    fields: [ 'id' ],
    op: 'unwrap'
  })
  
  // Состояние фильтров
  const [ filters, setFilters ] = useState<CourierShiftFilters>({
    date: new Date(), // По умолчанию сегодняшний день
    courierId: null
  })

  // Загрузка списка курьеров для селектора
  const { data: couriersData, loading: couriersLoading } = useFetchAllCouriers({
    pager: { page: 1, limit: 100 }
  })

  // Load all courier shifts
  // Фильтрация смен с 00:00 до 24:00 выбранного дня
  const dayStart = filters.date
    ? new Date(filters.date.setHours(0, 0, 0, 0)).getTime()
    : undefined
  const dayEnd = filters.date
    ? new Date(filters.date.setHours(23, 59, 59, 999)).toISOString()
    : undefined

  const { courierWorkshifts, loading, error } = useFetchAllCourierWorkShifts({
    pager: { page: 1, limit: 1000 },
    filter: {
      and: [
        {
          fields: [ 'plannedStartAt' ],
          op: 'gte',
          value: dayStart
        }
      ]
    }
  })

  // Заказы для таблицы (курьерские статусы)
  const { data: ordersData, loading: loadingOrders, error: errorOrders } = useFetchOrders({
    filter: {
      and: [
        {
          fields: [ 'status' ],
          op: 'contains',
          value: COURIER_STATUSES
        }
      ]
    }
  })

  // Комбинируем базовый фильтр по статусам логистики с пользовательским фильтром
  const logisticFilter = useMemo<NestedFilter>(() => {
    const baseFilter: NestedFilter = {
      fields: [ 'status' ],
      op: 'contains',
      value: ALL_LOGISTIC_STATUSES
    }

    if (userFilter.op === 'unwrap') {
      return { and: [ baseFilter ] }
    }

    return {
      and: [ baseFilter, userFilter ]
    }
  }, [ userFilter ])

  // Заказы для Kanban (все статусы логистики + пользовательский фильтр)
  const { data: logisticOrdersData, loading: logisticOrdersLoading } = useFetchOrders({
    filter: logisticFilter,
    allActive: true,
    pager: {
      page: 1,
      limit: 1000
    }
  })

  const logisticOrders = useMemo(
    () => logisticOrdersData?.orders?.edges || [],
    [ logisticOrdersData ]
  )

  // Статистика по статусам
  const { data: ordersByStatusData } = useFetchOrdersByStatus()

  const statsMap = useMemo(() => {
    const map: Record<number, number> = {}
    ordersByStatusData?.ordersByStatus?.forEach(group => {
      map[ group.status ] = group.count
    })
    return map
  }, [ ordersByStatusData ])

  const preparingCount = LOGISTIC_STATUS_GROUPS[0].statuses.reduce((sum, s) => sum + (statsMap[s] || 0), 0)
  const waitingCourierCount = statsMap[ ORDER_STATUS_WAITING_FOR_COURIER ] || 0
  const courierPickedCount = statsMap[ ORDER_STATUS_ACCEPTED_BY_COURIER ] || 0
  const deliveringCount = statsMap[ ORDER_STATUS_DEVIVERY_IN_PROGRESS ] || 0

  const orders = ordersData?.orders?.edges.map(order => ({
    id: order.id,
    address: order.address || 'Адрес не указан',
    status: order.status,
    courier: order.courier ? `${order.courier.first_name} ${order.courier.last_name}` : 'Не назначен'
  }))

  const preparedShifts: IShift[] = courierWorkshifts.map(shift => ({
    id: shift.id,
    courier: `${shift.courier.first_name} ${shift.courier.last_name}`,
    shift: `${new Date(shift.plannedStartAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - ${new Date(shift.plannedFinishAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`,
    status: shift.status,
    transport: shift.vehicle ? { type: shift.vehicle?.type?.code as IShift['transport']['type'], name: `${shift.vehicle.brand} ${shift.vehicle.model}`, licensePlate: shift.vehicle?.license_plate } : { type: 'foot', name: 'Пешком' },
    zones: [] // Здесь можно добавить зоны, если они есть в данных
  })
  )

  // Данные курьеров для селектора
  const courierOptions = useMemo(() => {
    if (!couriersData?.couriers?.edges) return []
    
    return couriersData.couriers.edges.map(courier => ({
      label: `${courier.first_name} ${courier.last_name}`,
      value: String(courier.id)
    }))
  }, [ couriersData ])

  // Обработчики фильтров
  const handleDateChange = useCallback((date: Date | null) => {
    setFilters(prev => ({ ...prev, date }))
  }, [])

  const handleCourierChange = useCallback((courierId: string | null) => {
    setFilters(prev => ({ ...prev, courierId }))
  }, [])

  const handleFilterChanged = useCallback((updatedFilter: NestedFilter) => {
    setUserFilter(updatedFilter)
  }, [])

  const handleResetFilters = useCallback(() => {
    setFilters({
      date: new Date(),
      courierId: null
    })
  }, [])

  const handleExpanded = (rowData: IShift) => {
    let open = false
    const nextExpandedRowKeys = []

    expandedRowKeys.forEach(key => {
      if (key === rowData.id) {
        open = true
      }
      else {
        nextExpandedRowKeys.push(key)
      }
    })

    if (!open) {
      nextExpandedRowKeys.push(rowData.id)
    }
    setExpandedRowKeys(nextExpandedRowKeys)
  }

  return (
    <div className='LogisticDashboard'>
      <h1 className="u-heading">
        Логистика
      </h1>
      <LogisticTopMenu />

      {/* Блоки статистики */}
      <Grid fluid style={{ marginBottom: 20, marginTop: 20 }}>
        <Row>
          <Col xs={24} sm={12} md={6}>
            <StatCard
              title="Готовятся"
              count={preparingCount}
              icon={<TimeOutline color="#fff" height="32px" width="32px" />}
              color="#ff9800"
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <StatCard
              title="Ожидают курьера"
              count={waitingCourierCount}
              icon={<CartOutline color="#fff" height="32px" width="32px" />}
              color="#2196f3"
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <StatCard
              title="Курьер забрал"
              count={courierPickedCount}
              icon={<CarOutline color="#fff" height="32px" width="32px" />}
              color="#9c27b0"
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <StatCard
              title="Доставляется"
              count={deliveringCount}
              icon={<CheckmarkCircleOutline color="#fff" height="32px" width="32px" />}
              color="#4caf50"
            />
          </Col>
        </Row>
      </Grid>

      <OrdersFilter onFilterChanged={handleFilterChanged} />

      {/* Kanban доска логистики */}
      <Panel bordered style={{ marginBottom: 20 }}>
        <LogisticKanbanBoard
          brandSlug={brandSlug}
          orders={logisticOrders}
          loading={logisticOrdersLoading}
          statusGroups={LOGISTIC_STATUS_GROUPS}
        />
      </Panel>
    </div>
  )
}